export const removeField = Symbol("REMOVE");
export const preserveField = Symbol("PRESERVE");

export type DeepTransformCallback = (
  /** The value to transform. Only objects with non-standard prototypes (ones other than Object, Array, and null) will be passed unless `testObjects` is `true`. */
  value: unknown,
  /** The key of the object above that points to this value. May be a number if the containing object is an array. */
  key: string | number,
  /** An array of keys to drill down from the top-level object to this property value.
   *
   * Caution: This array changes during iteration. Copy it with `.slice()` if values from specific iterations are referenced later. */
  path: (string | number)[],
  /** The parent object containing this property value. In general, `parent[key] === value` */
  parent: object | null
) => unknown;

const handledPrototypes = [Object.prototype, Array.prototype, null];

/** Deeply transform the passed value by running every scalar value and/or object value through a conversion function.
 *
 * - Object properties are recursively iterated.
 * - Recursive structures are safely transformed.
 * - Properties can be removed / array indices can be spliced out by returning `deepTransform.removeField` from the callback.
 * - Properties can be left unchanged by returning `deepTransform.preserveField` from the callback.
 *
 * @param value The structured object or arbitrary value to transform.
 * @param callback The callback that will receive values and return new values to which to transform them.
 * @param options Options for the transform process.
 * @return Returns a deep copy of the object received, transformed as defined by the callback.
 * Note, if the callback returns `deepTransform.preserveField` for any objects encountered, these objects will not be copied.
 *
 * ---
 *
 * ### Signature of `callback`:
 *
 * - *param* `value` The value to transform. Only objects with non-standard prototypes (ones other than Object, Array, and null) will be passed unless `testObjects` is `true`.
 * - *param* `key` The key of the object above that points to this value. May be a number if the containing object is an array.
 * - *param* `path` An array of keys to drill down from the top-level object to this property value.
 * - *param* `parent` The parent object containing this property value. In general, `parent[key] === value`
 * - *return* Returns the new value for the property / field, `deepTransform.removeField` to remove the field, or `deepTransform.preserveField` to leave it unchanged.
 *
 * ---
 *
 * ### Options:
 *
 * - `testObjects` Boolean. Default `false`.
 *
 * Objects with Object, Array, and null prototypes are sub-traversed, but not passed to the transform callback.
 * By setting `testObjects` to `true`, all objects - including arrays and plain objects - are passed to the callback.
 * The callback must then return `deepTransform.preserveField` to continue sub-traversing the object instead of directly transforming it.
 *
 * - `preserveArrayIndices` Boolean. Default `false`.
 *
 * If a transformation removes a value from an array by returning `removeField`, it will be spliced out, pulling back the remaining elements.
 * If `preserveArrayIndices` is set to `true`, however, these elements will be left empty instead of spliced.
 * Thus, future elements will retain their index, and these indices will give `undefined`. (Note that `for in` loops skip empty array entries.)
 *
 * - `nullObjects` Boolean. Default `false`.
 *
 * By default, plain objects with a prototype of Object.prototype are used in the cloning process.
 * If `nullObjects` is set to `true`, `null` will be used as the prototype for objects in the returned structure.
 * This is safer for structures that can contain arbitrary hash-tables which could include keys like `"__proto__"`.
 * */
export const deepTransform = Object.assign(
  (
    /** The structured object or arbitrary value to transform. */
    value: unknown,
    /** The callback that will receive values and return new values to which to transform them. */
    callback: DeepTransformCallback,
    /** Options for the transform process. */
    {
      testObjects = false,
      preserveArrayIndices = false,
      nullObjects = false,
    }: {
      /** Default: `false`
       *
       * Objects with Object, Array, and null prototypes are sub-traversed, but not passed to the transform callback.
       * By setting `testObjects` to `true`, all objects - including arrays and plain objects - are passed to the callback.
       * The callback must then return `deepTransform.preserveField` to continue sub-traversing the object instead of directly transforming it. */
      testObjects?: boolean;
      /** Default: `false`
       *
       * If a transformation removes a value from an array by returning `removeField`, it will be spliced out, pulling back the remaining elements.
       * If `preserveArrayIndices` is set to `true`, however, these elements will be left empty instead of spliced.
       * Thus, future elements will retain their index, and these indices will give `undefined`. (Note that `for in` loops skip empty array entries.) */
      preserveArrayIndices?: boolean;
      /** Default: `false`
       *
       * By default, plain objects with a prototype of Object.prototype are used in the cloning process.
       * If `nullObjects` is set to `true`, `null` will be used as the prototype for objects in the returned structure.
       * This is safer for structures that can contain arbitrary hash-tables which could include keys like `"__proto__"`. */
      nullObjects?: boolean;
    } = {}
  ): unknown => {
    // deep nested property path
    const path: (string | number)[] = [];

    // map of untransformed objects to respective transformed versions
    const seen = new Map();

    // recursive transform function
    const iterate = (
      value: unknown,
      key: string | number,
      parent: object | null
    ) => {
      if (
        !value ||
        typeof value !== "object" ||
        !handledPrototypes.includes(Object.getPrototypeOf(value))
      ) {
        // transform regular values
        return callback(value, key, path, parent);
      }

      // transform object-types

      // if object has been transformed, avoid recursion and return previously transformed version
      if (seen.has(value)) return seen.get(value);

      if (testObjects) {
        // pass objects through transform test
        const returnValue = callback(value, key, path, parent);
        if (returnValue !== preserveField) {
          // note, returnValue might also be removeField, meaning all other property references to the same object will be removed:
          seen.set(value, returnValue);
          return returnValue;
        }
        // if preserveField is returned, continue checking properties of object
      }

      if (Array.isArray(value)) {
        // transform arrays
        const newArray: unknown[] = [];
        seen.set(value, newArray);
        let j = 0;

        // iterate indices
        for (let i = 0; i < value.length; i++) {
          path.push(i);

          const returnValue = iterate(value[i], i, value);
          if (returnValue === preserveField) {
            newArray[j++] = value[i];
          } else if (returnValue === removeField) {
            if (preserveArrayIndices) j++;
          } else {
            newArray[j++] = returnValue;
          }

          path.pop();
        }

        return newArray;
      } else {
        // transform objects
        const newObject: Record<string, unknown> = nullObjects
          ? Object.create(null)
          : {};
        seen.set(value, newObject);

        // iterate keys
        for (const key in value) {
          path.push(key);

          const returnValue = iterate(value[key as never], key, value);
          if (returnValue === preserveField) {
            newObject[key] = value[key as never];
          } else if (returnValue !== removeField) {
            newObject[key] = returnValue;
          }

          path.pop();
        }

        return newObject;
      }
    };

    // run transform and process result
    const returnValue = iterate(value, "", null);
    if (returnValue === removeField) {
      return undefined;
    } else if (returnValue === preserveField) {
      return value;
    }
    return returnValue;
  },
  { removeField, preserveField }
);
